/**
 * API Documentation Template Form
 * API specs, endpoints, and examples
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const APIDocumentationForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    api_name: '', version: '', base_url: '', date: new Date().toISOString().split('T')[0], prepared_by: '',
    description: '', authentication: '', rate_limiting: '', error_handling: '',
    endpoints: [{ method: 'GET', path: '', summary: '', description: '', request_body: '', request_example: '', response_body: '', response_example: '', authentication: '', rate_limit: '' }],
    data_models: [], code_examples: [], changelog: [],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    api_name: 'User Management API',
    version: 'v2',
    base_url: 'https://api.company.com/v2',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'API Team',
    description: 'RESTful API for managing users, authentication, and permissions. This API follows REST conventions and returns JSON responses.',
    authentication: 'Bearer token authentication. Include header: Authorization: Bearer <token>. Tokens can be obtained via POST /auth/login.',
    rate_limiting: '1000 requests per minute per API key. Rate limit headers included in response: X-RateLimit-Limit, X-RateLimit-Remaining, X-RateLimit-Reset.',
    error_handling: 'Errors return appropriate HTTP status codes with JSON body containing "error" and "message" fields. Example: {"error": "unauthorized", "message": "Invalid or expired token"}',
    endpoints: [
      { method: 'GET', path: '/users', summary: 'List all users', description: 'Returns paginated list of users. Supports filtering by role, status, and search query.', request_body: '', request_example: 'GET /users?page=1&limit=20&role=admin', response_body: '{ data: User[], pagination: { page, limit, total } }', response_example: '{"data": [{"id": "usr_123", "email": "john@example.com", "role": "admin"}], "pagination": {"page": 1, "limit": 20, "total": 150}}', authentication: 'Required', rate_limit: '100/min' },
      { method: 'GET', path: '/users/:id', summary: 'Get user by ID', description: 'Returns a single user by their unique ID.', request_body: '', request_example: 'GET /users/usr_123', response_body: 'User object', response_example: '{"id": "usr_123", "email": "john@example.com", "name": "John Doe", "role": "admin", "created_at": "2025-01-01T00:00:00Z"}', authentication: 'Required', rate_limit: '100/min' },
      { method: 'POST', path: '/users', summary: 'Create new user', description: 'Creates a new user account. Email must be unique.', request_body: '{ email: string, name: string, password: string, role?: string }', request_example: '{"email": "jane@example.com", "name": "Jane Doe", "password": "securePass123", "role": "user"}', response_body: 'Created User object', response_example: '{"id": "usr_456", "email": "jane@example.com", "name": "Jane Doe", "role": "user", "created_at": "2025-01-15T10:30:00Z"}', authentication: 'Required (Admin)', rate_limit: '20/min' },
      { method: 'PUT', path: '/users/:id', summary: 'Update user', description: 'Updates an existing user. Only provided fields are updated.', request_body: '{ name?: string, role?: string, status?: string }', request_example: '{"name": "John Smith", "role": "manager"}', response_body: 'Updated User object', response_example: '{"id": "usr_123", "email": "john@example.com", "name": "John Smith", "role": "manager"}', authentication: 'Required', rate_limit: '50/min' },
      { method: 'DELETE', path: '/users/:id', summary: 'Delete user', description: 'Soft deletes a user account. Can be restored within 30 days.', request_body: '', request_example: 'DELETE /users/usr_123', response_body: '{ success: boolean, message: string }', response_example: '{"success": true, "message": "User deleted successfully"}', authentication: 'Required (Admin)', rate_limit: '10/min' }
    ],
    data_models: [
      { name: 'User', fields: 'id (string), email (string), name (string), role (enum: user|admin|manager), status (enum: active|inactive|deleted), created_at (datetime), updated_at (datetime)' }
    ],
    code_examples: [
      { language: 'cURL', code: 'curl -X GET "https://api.company.com/v2/users" -H "Authorization: Bearer <token>"' },
      { language: 'JavaScript', code: 'const response = await fetch("https://api.company.com/v2/users", { headers: { "Authorization": "Bearer " + token } });' }
    ],
    changelog: [
      { version: 'v2.1.0', date: '2025-01-15', changes: 'Added pagination to /users endpoint, added role filter' },
      { version: 'v2.0.0', date: '2024-12-01', changes: 'Breaking: Changed authentication from API key to Bearer token' }
    ],
    rai_assignments: [],
    followups: []
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "API Documentation Draft";
      const payload = {
        name: draftName,
        template_type: "API Documentation",
        template_id: "api-documentation",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "a-p-i-documentation", "A P I Documentation", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("a-p-i-documentation", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/api-documentation`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `API_Documentation_${formData.api_name.replace(/\s+/g, '_') || 'Document'}_${formData.version}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "API Documentation",
        formData,
        settings: activeSettings || {},
        templateType: "API Documentation",
        templateId: "api-documentation",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addEndpoint = () => {
    setFormData({
      ...formData,
      endpoints: [...formData.endpoints, { method: 'GET', path: '', summary: '', description: '', request_body: '', request_example: '', response_body: '', response_example: '', authentication: '', rate_limit: '' }]
    });
  };

  const updateEndpoint = (index, field, value) => {
    const endpoints = [...formData.endpoints];
    endpoints[index] = { ...endpoints[index], [field]: value };
    setFormData({ ...formData, endpoints });
  };

  const removeEndpoint = (index) => {
    setFormData({ ...formData, endpoints: formData.endpoints.filter((_, i) => i !== index) });
  };

  const methods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
  const methodColors = { GET: 'bg-green-100 text-green-800', POST: 'bg-blue-100 text-blue-800', PUT: 'bg-yellow-100 text-yellow-800', PATCH: 'bg-orange-100 text-orange-800', DELETE: 'bg-red-100 text-red-800' };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">API Documentation</h1><p className="text-sm text-gray-500">API specs, endpoints, and examples</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        <Card>
          <CardHeader><CardTitle>API Information</CardTitle></CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="col-span-2"><Label>API Name *</Label><Input value={formData.api_name} onChange={(e) => setFormData({...formData, api_name: e.target.value})} placeholder="User Management API" /></div>
              <div><Label>Version *</Label><Input value={formData.version} onChange={(e) => setFormData({...formData, version: e.target.value})} placeholder="v2" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Base URL *</Label><Input value={formData.base_url} onChange={(e) => setFormData({...formData, base_url: e.target.value})} placeholder="https://api.example.com/v2" /></div>
              <div><Label>Prepared By</Label><Input value={formData.prepared_by} onChange={(e) => setFormData({...formData, prepared_by: e.target.value})} placeholder="API Team" /></div>
            </div>

            <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Overview of what this API does..." rows={2} /></div>
            <div><Label>Authentication</Label><Textarea value={formData.authentication} onChange={(e) => setFormData({...formData, authentication: e.target.value})} placeholder="How to authenticate requests..." rows={2} /></div>
            <div><Label>Rate Limiting</Label><Input value={formData.rate_limiting} onChange={(e) => setFormData({...formData, rate_limiting: e.target.value})} placeholder="1000 requests/minute per API key" /></div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Endpoints ({formData.endpoints.length})</Label>
              <div className="space-y-4 mt-3">
                {formData.endpoints.map((ep, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-lg border">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${methodColors[ep.method]}`}>{ep.method}</span>
                        <span className="font-mono text-sm">{ep.path || '/path'}</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => removeEndpoint(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div><Label className="text-xs">Method</Label>
                        <Select value={ep.method} onValueChange={(v) => updateEndpoint(idx, 'method', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{methods.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2"><Label className="text-xs">Path *</Label><Input value={ep.path} onChange={(e) => updateEndpoint(idx, 'path', e.target.value)} placeholder="/users/:id" className="text-sm font-mono" /></div>
                      <div><Label className="text-xs">Summary</Label><Input value={ep.summary} onChange={(e) => updateEndpoint(idx, 'summary', e.target.value)} placeholder="Get user by ID" className="text-sm" /></div>
                    </div>

                    <div className="mt-3"><Label className="text-xs">Description</Label><Textarea value={ep.description} onChange={(e) => updateEndpoint(idx, 'description', e.target.value)} placeholder="Detailed description..." rows={2} className="text-sm" /></div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <div><Label className="text-xs">Request Body Schema</Label><Textarea value={ep.request_body} onChange={(e) => updateEndpoint(idx, 'request_body', e.target.value)} placeholder='{ email: string, name: string }' rows={2} className="text-sm font-mono" /></div>
                      <div><Label className="text-xs">Request Example</Label><Textarea value={ep.request_example} onChange={(e) => updateEndpoint(idx, 'request_example', e.target.value)} placeholder='{"email": "user@example.com"}' rows={2} className="text-sm font-mono" /></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      <div><Label className="text-xs">Response Body Schema</Label><Textarea value={ep.response_body} onChange={(e) => updateEndpoint(idx, 'response_body', e.target.value)} placeholder='User object' rows={2} className="text-sm font-mono" /></div>
                      <div><Label className="text-xs">Response Example</Label><Textarea value={ep.response_example} onChange={(e) => updateEndpoint(idx, 'response_example', e.target.value)} placeholder='{"id": "usr_123", ...}' rows={2} className="text-sm font-mono" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addEndpoint} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Endpoint</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
              <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download DOCX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default APIDocumentationForm;
